import { useEffect, useRef, type Dispatch } from 'react';
import type { EffortScale, Exercise } from '@heraion/shared';

import { amrapNeedsRepsAt } from './amrapGate';
import { nextCalibrationSet } from './calibrationRamp';
import { resolveLoadShape } from './state/resolveLoadShape';
import { appendCalibrationSet } from './state/calibrationReducer';
import {
  loadPlateLoadShape,
  useRoundingContextRef,
  type PlateLoadShape,
} from './state/plateConfig';
import { resolveRestTransition } from './restTransition';
import type { GroupMember } from './supersetNotebook';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import type { LoggerMode } from './loggerMode';
import type { TimelineLoggerInput } from './loggerContext';
import type { useAmrapConfirm } from './useAmrapConfirm';

// The completion leg of the timeline logger: the LG-28 gate, commitSet and
// onLogSet. Moved out of useTimelineLogger to keep that hook under the
// 500-line cap; the code is the code that lived inline there.
export function useCommitSet({
  entries,
  dispatch,
  mode,
  activeEntryIdx,
  boundSetIdx,
  groupMembers,
  cycleSetPill,
  restAdvance,
  restInvite,
  setSelectedSetIdx,
  amrapConfirm,
  barLoading,
  effortScale,
}: {
  entries: readonly DraftEntry[];
  dispatch: Dispatch<LoggerAction>;
  mode: LoggerMode;
  activeEntryIdx: number;
  boundSetIdx: number;
  groupMembers: readonly GroupMember[];
  cycleSetPill: TimelineLoggerInput['cycleSetPill'];
  restAdvance: TimelineLoggerInput['restAdvance'];
  restInvite: TimelineLoggerInput['restInvite'];
  setSelectedSetIdx: (idx: number | null) => void;
  amrapConfirm: ReturnType<typeof useAmrapConfirm>;
  /** #353: the active exercise's catalog loading shape (absent: standard),
   *  for the ramp's loadable rung. */
  barLoading?: Exercise['barLoading'];
  /** The athlete's effort scale: the ramp reads effort as the save does. */
  effortScale?: EffortScale;
}) {
  const roundingContextRef = useRoundingContextRef();
  // The load shape the athlete SAVED for the active exercise, read async from
  // the same store as the plan hydration and the swap; it wins over the
  // catalog's (`stored ?? barLoading`). Null until read: the catalog's.
  const activeSlug = entries[activeEntryIdx]?.exerciseId;
  const storedShapeRef = useRef<{ slug: string; shape: PlateLoadShape | null } | null>(null);
  useEffect(() => {
    if (!activeSlug) return;
    let cancelled = false;
    void loadPlateLoadShape(activeSlug).then((shape) => {
      if (!cancelled) storedShapeRef.current = { slug: activeSlug, shape };
    });
    return () => {
      cancelled = true;
    };
  }, [activeSlug]);
  // LG-28 gate, shared by EVERY completion leg: onLogFail forgot it once and
  // silently completed the set as an ordinary one. Rationale in amrapGate.ts.
  const blockedOnReps = (i: number) =>
    amrapNeedsRepsAt(entries, activeEntryIdx, i);

  // Log → decide → hand over. Shared by onLogSet and onLogFail: both complete
  // the bound set through cycleSetPill and both advance the same way, which is
  // the 2026-07-20 rule that a fail is a normal outcome and not an error.
  // ⚠️ The transition is resolved against the PRE-dispatch draft, exactly as
  // the advance always was: the just-logged set still reads pending there, and
  // both resolvers exclude it by index.
  // `fail` rides into cycleSetPill for the rest invite only: the fail leg
  // itself is the caller's own cycle_set, dispatched after this returns.
  const commitSet = (
    loggedIdx: number,
    opts?: { rir?: number; fail?: boolean },
  ): { prFlash: boolean } => {
    if (blockedOnReps(loggedIdx)) return { prFlash: false };
    // #353: the calibration ramp's next set joins the entry BEFORE the
    // transition reads it, so the focus stays on the ramp.
    const entry = entries[activeEntryIdx];
    const stored = storedShapeRef.current;
    const rampSet = entry
      ? nextCalibrationSet(
          entry,
          loggedIdx,
          opts ?? {},
          {
            ctx: roundingContextRef.current,
            barLoading: resolveLoadShape(
              stored && stored.slug === entry.exerciseId ? stored.shape : null,
              barLoading,
            ),
          },
          effortScale,
        )
      : null;
    if (rampSet) {
      dispatch({ type: 'append_calibration_set', entryIdx: activeEntryIdx, set: rampSet });
    }
    const seen = rampSet
      ? entries.map((e, i) =>
          i === activeEntryIdx ? appendCalibrationSet(e, rampSet) : e,
        )
      : entries;
    // A LIVE-CUE question: only live logging sits through a rest, so a
    // past-session edit and a prescription both collapse the transition.
    const transition = mode !== 'log'
      ? null
      : resolveRestTransition({
          entries: seen,
          entryIdx: activeEntryIdx,
          loggedSetIdx: loggedIdx,
          groupMembers,
        });
    const committed = cycleSetPill(activeEntryIdx, loggedIdx, {
      ...(opts?.rir !== undefined ? { rir: opts.rir } : {}),
      ...(opts?.fail === true ? { fail: true } : {}),
      ...(transition ? { transition } : {}),
    });
    if (transition === null) {
      setSelectedSetIdx(null);
      return committed;
    }
    // Every armed rest replaces the offer, so a set that earns none clears
    // the one the previous set left behind.
    if (committed.restArmed) restInvite?.offer(committed.restInviteSec ?? 0);
    // A rest is running ⇒ hold the binding on the set just logged, so the card
    // in front of you is still the one you're resting from. Cleared when the
    // transition applies. No rest ⇒ nothing to wait for, move now.
    if (committed.restArmed) setSelectedSetIdx(loggedIdx);
    else setSelectedSetIdx(null);
    restAdvance.arm(transition, committed.restArmed);
    return committed;
  };

  // rir (AP-0b): the effort meter completes the set AND records the effort in
  // one tap. The write goes through the same update_set patch the old effort
  // input used ({rir, rpe: ''}, one canonical field), queued BEFORE cycle_set
  // so the reducer lands both in order; the override also rides into
  // cycleSetPill so its MAX-regression check reads the tapped rir, not the
  // stale draft. Guarded on typeof: a bare onPress={onLogSet} would pass the
  // press event here. Returns cycleSetPill's { prFlash } for the gold pulse.
  // `durationSec` (MX-5 slice 3) is the held time when that same tap stopped a
  // running hold/cardio timer: it joins the SAME patch, so one gesture is one
  // write and the completion never lands on a set whose seconds are still
  // missing. No override into cycleSetPill for it, unlike rir: the PR check
  // builds from reps/weight (buildWorkoutSetFromDraft), so a duration set is
  // never PR-eligible and the value would go unread.
  const onLogSet = (rir?: number, durationSec?: number) => {
    if (activeEntryIdx < 0 || boundSetIdx < 0) return undefined;
    const loggedIdx = boundSetIdx;
    // Before the effort dispatch: the band tap writes RIR first.
    if (blockedOnReps(loggedIdx)) return undefined;
    const tappedRir = typeof rir === 'number' ? rir : undefined;
    const heldSec =
      typeof durationSec === 'number' && durationSec > 0 ? durationSec : undefined;
    if (tappedRir !== undefined || heldSec !== undefined) {
      dispatch({
        type: 'update_set',
        entryIdx: activeEntryIdx,
        setIdx: loggedIdx,
        patch: {
          ...(tappedRir !== undefined ? { rir: String(tappedRir), rpe: '' } : {}),
          ...(heldSec !== undefined ? { duration: String(heldSec) } : {}),
        },
      });
    }
    // Completes the bound set: PR check + rest timer + bodyweight marker, all
    // via the same hook the card logger uses. No add_set here: the next
    // pending set (planned, or one the athlete adds via ＋) becomes the bound
    // target once the rest hands the focus over. The superset round-robin is
    // no longer resolved here: it is one branch of resolveRestTransition, so
    // the hop and the 4s that precedes it come from one answer, not two.
    const committed = commitSet(loggedIdx, {
      ...(tappedRir !== undefined ? { rir: tappedRir } : {}),
    });
    // Ask about a prescribed AMRAP AFTER the commit, so the question is about a
    // set that is already logged: answering it is an edit, and dismissing it
    // leaves an ordinary completed set behind rather than an unfinished one.
    amrapConfirm.maybePrompt(activeEntryIdx, loggedIdx);
    return committed;
  };

  return { blockedOnReps, commitSet, onLogSet };
}
